import React, { useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  Chip,
} from "@mui/material";
import { useUpdateDriverLead } from "../../../hooks/use-driver";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import DocumentChecklist from "../../preOnboarding/documentChecklist/DocumentChecklist";

const EMI_CADENCES = [
  { key: "monthly", label: "EMI" },
  { key: "weekly", label: "EWI" },
  { key: "daily", label: "EDI" },
];

const getFinancierKey = (option, index) =>
  option?.financier ||
  option?.code ||
  option?.displayName ||
  `financier-${index}`;

const safeAmount = (value) =>
  value != null && !Number.isNaN(Number(value))
    ? `₹${Number(value).toLocaleString("en-IN")}`
    : "--";

// const safeInterestRate = (value) => {
//   if (value == null || Number.isNaN(Number(value))) return "--";
//   const rate = Number(value);
//   const percent = rate <= 1 ? rate * 100 : rate;
//   return `${percent % 1 === 0 ? percent : percent.toFixed(2)}%`;
// };

const safeEmiValue = (emi, cadenceKey) => {
  if (emi == null) return "--";

  if (typeof emi === "object") {
    return safeAmount(emi[cadenceKey]);
  }

  const monthly = Number(emi);
  if (!monthly) return "--";

  if (cadenceKey === "monthly") return safeAmount(monthly);

  const weekly = Math.ceil((monthly * 12) / 52 / 5) * 5;
  if (cadenceKey === "weekly") return safeAmount(weekly);

  const daily = Math.ceil(weekly / 7 / 5) * 5;
  return safeAmount(daily);
};

const FinancierSelectionStep = ({
  data,
  onChange,
  showErrors,
  allFormData,
  leadId,
  leadData,
  setLeadData,
  onSaveFinancier,
}) => {
  const eligibleFinanciers = leadData?.eligibleFinanciers;
  const loanSummary = leadData?.loanSummary;
  const crifReportUrl = leadData?.documentUpload?.crifReportUrl;
  const cibilScore = leadData?.cibilScore;

  const isFinancierChangeRequest = leadData?.isFinancierChangeRequest;

  const [selectedFinancierKey, setSelectedFinancierKey] = useState(
    data.selectedFinancier || "",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistFinancier, setChecklistFinancier] = useState(null);

  const { mutate: updateDriverLead } = useUpdateDriverLead();

  const handleSelect = (financierKey) => {
    setSelectedFinancierKey(financierKey);
    onChange("selectedFinancier", financierKey);
  };

  const handleContinue = async (option, index) => {
    const financierKey = getFinancierKey(option, index);

    setSaveError(null);
    setIsSaving(true);
    setSelectedFinancierKey(financierKey);
    onChange("selectedFinancier", financierKey);

    if (!leadId) {
      setIsSaving(false);
      return;
    }

    try {
      await onSaveFinancier(option);
    } catch (err) {
      setSaveError(getErrorMessage(err, "Failed to save selected financier"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChecklist = (financierKey) => {
    setChecklistFinancier(financierKey);
    setChecklistOpen(true);
  };

  const handleCloseChecklist = () => {
    setChecklistOpen(false);
    setChecklistFinancier(null);
  };

  const loanSummaryExists = loanSummary && Object.keys(loanSummary)?.length > 0;

  const InfoPill = ({ label, children }) => (
    <Box
      sx={{
        flex: "1 1 260px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        backgroundColor: "#F3F5F4",
        border: "1px solid #E8ECEB",
        borderRadius: "12px",
        px: 2,
        py: 1.25,
      }}
    >
      <Typography sx={{ fontSize: 11, color: "#66766F", fontWeight: 600 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );

  const cibilCrifInfoBar = (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
      <InfoPill label="CIBIL Score">
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#12181A" }}>
          {cibilScore || "--"}
        </Typography>
      </InfoPill>

      <InfoPill label="CRIF Report">
        {crifReportUrl ? (
          typeof crifReportUrl === "string" ? (
            <Typography
              component="a"
              href={crifReportUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: "#0E7A6E",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View Report
            </Typography>
          ) : (
            <Typography
              sx={{ fontSize: 14, fontWeight: 700, color: "#12181A" }}
            >
              {crifReportUrl?.name || "Uploaded"}
            </Typography>
          )
        ) : (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#12181A" }}>
            --
          </Typography>
        )}
      </InfoPill>
    </Box>
  );

  if (loanSummaryExists && !isFinancierChangeRequest) {
    return (
      <>
        {cibilCrifInfoBar}
        <Card
          sx={{
            borderRadius: 3,
            border: "2px solid #0E7A6E",
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1.5,
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: 16, color: "#12181A" }}
              >
                {loanSummary.financier || "Selected financier"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: "#0E7A6E",
                  backgroundColor: "#E6F5F2",
                  borderRadius: 999,
                  px: 1.25,
                  py: 0.25,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                }}
              >
                Selected
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.75,
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: "#66766F" }}>
                Loan Amount
              </Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                {safeAmount(loanSummary.loanAmount)}
              </Typography>
            </Box>

            {/* <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.75,
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: "#66766F" }}>
                Interest Rate
              </Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                {safeInterestRate(loanSummary.interestRate)}
              </Typography>
            </Box> */}

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}
            >
              <Typography sx={{ fontSize: 12.5, color: "#66766F" }}>
                Tenure
              </Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                {loanSummary.tenure != null
                  ? `${loanSummary.tenure} months`
                  : "--"}
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: "#E6F5F2",
                borderRadius: 2,
                p: 1.5,
                mb: 2,
                display: "flex",
              }}
            >
              {EMI_CADENCES?.map((cadence, cadenceIndex) => (
                <Box
                  key={cadence.key}
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    borderLeft:
                      cadenceIndex === 0
                        ? "none"
                        : "1px solid rgba(11,95,86,0.15)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: "#0B5F56",
                      fontWeight: 650,
                      textTransform: "uppercase",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {cadence.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: cadence.key === "monthly" ? 17 : 14,
                      fontWeight: 700,
                      color: "#0B5F56",
                      mt: 0.25,
                    }}
                  >
                    {safeEmiValue(loanSummary.emi, cadence.key)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="outlined"
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                onClick={() => handleOpenChecklist(loanSummary.financier)}
              >
                Doc Checklist
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Dialog
          open={checklistOpen}
          onClose={handleCloseChecklist}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
          <DialogContent dividers sx={{ p: 0, px: 3 }}>
            <DocumentChecklist
              financierType={checklistFinancier}
              isActionButton={false}
              actions={[<Button onClick={handleCloseChecklist}>Close</Button>]}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (!eligibleFinanciers?.length) {
    return (
      <Alert severity="error">
        Eligible financiers have not been calculated for this driver yet. Please
        wait for the admin to complete the process after the CIBIL score and
        CRIF report are available.
      </Alert>
    );
  }

  return (
    <>
      {cibilCrifInfoBar}

      {showErrors && !data.selectedFinancier && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Please select a financier
        </Alert>
      )}

      <Grid container spacing={2}>
        {eligibleFinanciers?.map((option, index) => {
          const financierKey = getFinancierKey(option, index);
          const financier = option?.financier;
          const isSelected = selectedFinancierKey === financierKey;
          const isThisSaving = isSaving && isSelected;

          const rejectedFinanciers = Array.isArray(leadData?.rejectedFinanciers)
            ? leadData.rejectedFinanciers
            : [];

          const isRejected = rejectedFinanciers.includes(financier);

          return (
            <Grid item xs={12} sm={6} lg={4} key={financierKey}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: isSelected
                    ? "2px solid #0E7A6E"
                    : "1px solid #E6ECEA",
                  boxShadow: "none",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "border-color 0.15s ease",
                }}
              >
                <CardContent
                  sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      justifyContent: "space-between",
                    }}
                  >
                    {/* <Radio
                      checked={isSelected}
                      onChange={() => handleSelect(financierKey)}
                      sx={{
                        p: 0,
                        mr: 1,
                        color: "#0E7A6E",
                        "&.Mui-checked": { color: "#0E7A6E" },
                      }}
                    /> */}
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: "#12181A",
                      }}
                    >
                      {option?.financier ||
                        option?.displayName ||
                        "Unknown financier"}
                    </Typography>

                    {isRejected && (
                      <Chip
                        label="Rejected"
                        size="small"
                        sx={{
                          bgcolor: "#fdecea",
                          color: "#d32f2f",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          borderRadius: "6px",
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.75,
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, color: "#66766F" }}>
                      Loan Amount
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                      {safeAmount(option?.loanAmount)}
                    </Typography>
                  </Box>

                  {/* <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.75,
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, color: "#66766F" }}>
                      Interest Rate
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                      {safeInterestRate(option?.interestRate)}
                    </Typography>
                  </Box> */}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, color: "#66766F" }}>
                      Tenure
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                      {option?.tenure != null
                        ? `${option.tenure} months`
                        : "--"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: "#E6F5F2",
                      borderRadius: 2,
                      p: 1.5,
                      mb: 2,
                      display: "flex",
                    }}
                  >
                    {EMI_CADENCES?.map((cadence, cadenceIndex) => (
                      <Box
                        key={cadence.key}
                        sx={{
                          flex: 1,
                          textAlign: "center",
                          borderLeft:
                            cadenceIndex === 0
                              ? "none"
                              : "1px solid rgba(11,95,86,0.15)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10.5,
                            color: "#0B5F56",
                            fontWeight: 650,
                            textTransform: "uppercase",
                            letterSpacing: "0.02em",
                          }}
                        >
                          {cadence.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: cadence.key === "monthly" ? 17 : 14,
                            fontWeight: 700,
                            color: "#0B5F56",
                            mt: 0.25,
                          }}
                        >
                          {safeEmiValue(option?.emi, cadence.key)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    disabled={isSaving || !leadId || isRejected}
                    onClick={() => handleContinue(option, index)}
                    startIcon={
                      isThisSaving ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : null
                    }
                    sx={{
                      mt: "auto",
                      borderRadius: 2,
                      backgroundColor: "#0E7A6E",
                      fontWeight: 600,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#0B5F56",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {isThisSaving ? "Selecting..." : "Select"}
                  </Button>

                  <Button
                    disabled={isSaving || isRejected}
                    variant="outlined"
                    fullWidth
                    sx={{
                      mt: 1,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                    onClick={() => handleOpenChecklist(financierKey)}
                  >
                    Doc Checklist
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={checklistOpen}
        onClose={handleCloseChecklist}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          dividers
          sx={{
            p: 0,
            px: 3,
          }}
        >
          <DocumentChecklist
            financierType={checklistFinancier}
            isActionButton={false}
            actions={[<Button onClick={handleCloseChecklist}>Close</Button>]}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FinancierSelectionStep;
